// @ts-nocheck — verbatim port from xop-edge-staff. See src/crm/README.md.
import { useState, useEffect } from 'react'
import {
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  Dialog,
  DialogContent,
  DialogTitle,
  FormLabel,
  TextField,
  Typography,
} from "@mui/material"
import { DatePicker } from "@/components/ui/date-picker"
import { toast } from "@/components/ui/use-toast"

export interface Deal {
  id: string
  name: string
  amount: number
  maintenance_percentage?: number
  maintenance_amount?: number
  start_date?: string
  renewal_date?: string
  customer: {
    id: string
    full_name: string
    company_name?: string
  }
}



interface EditDealModalProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  deal?: Deal
  customerName?: string
  companyName?: string
  onSave: (dealData: Partial<Deal>) => Promise<void>
}

export function EditDealModal({ open, onOpenChange, deal, customerName, companyName, onSave }: EditDealModalProps) {
  const [loading, setLoading] = useState(false)

  // Handle modal close with cleanup
  const handleClose = (isOpen: boolean) => {
    if (!isOpen && !loading) {
      // Reset form when modal closes (unless we're still loading)
      setTimeout(() => {
        setTitle('')
        setAmount('0') // Reset to 0
        setMaintenancePercentage('')
        setStartDate(new Date().toISOString().split('T')[0])
        setRenewalDate('')
      }, 100)
    }
    onOpenChange(isOpen)
  }

  // Deal form state
  const [title, setTitle] = useState('')
  const [amount, setAmount] = useState('')
  const [maintenancePercentage, setMaintenancePercentage] = useState('')
  const [startDate, setStartDate] = useState('')
  const [renewalDate, setRenewalDate] = useState('')



  // Initialize form when deal changes
  useEffect(() => {

    if (deal) {

      setTitle(deal.name || '')
      setAmount(deal.amount?.toString() || '')
      setMaintenancePercentage(deal.maintenance_percentage?.toString() || '')
      setStartDate(deal.start_date || new Date().toISOString().split('T')[0])
      setRenewalDate(deal.renewal_date || '')
    } else {
      // Reset form for new deal
      setTitle('')
      setAmount('0') // Default to 0 for new deals
      setMaintenancePercentage('')
      setStartDate(new Date().toISOString().split('T')[0]) // Default to today
      setRenewalDate('')
    }
  }, [deal, open])





  // Helper function to generate default deal title
  const generateDefaultTitle = (): string => {
    if (deal) {
      // For editing, use existing title
      return deal.name || ''
    }
    // For new deals, generate based on customer/company name
    const name = companyName || customerName || 'Customer'
    return `Agreement for ${name}`
  }

  const handleSave = async () => {
    // Auto-generate title if not provided (only for new deals)
    const finalTitle = title.trim() || (deal ? deal.name : generateDefaultTitle())

    if (!finalTitle) {
      toast({
        title: "Validation Error",
        description: "Deal title is required",
        variant: "destructive"
      })
      return
    }

    // Allow 0 prices - remove the > 0 validation
    if (!amount || parseFloat(amount) < 0) {
      toast({
        title: "Validation Error",
        description: "Equipment price cannot be negative",
        variant: "destructive"
      })
      return
    }

    const maintenancePercent = maintenancePercentage ? parseFloat(maintenancePercentage) : undefined
    if (maintenancePercent !== undefined && (maintenancePercent < 0 || maintenancePercent > 100)) {
      toast({
        title: "Validation Error",
        description: "Maintenance percentage must be between 0 and 100",
        variant: "destructive"
      })
      return
    }

    if (!startDate) {
      toast({
        title: "Validation Error",
        description: "Start date is required",
        variant: "destructive"
      })
      return
    }

    try {
      setLoading(true)

      const dealData: Partial<Deal> = {
        name: finalTitle,
        amount: parseFloat(amount) || 0, // Allow 0
        maintenance_percentage: maintenancePercent,
        start_date: startDate,
        renewal_date: renewalDate && renewalDate.trim() ? renewalDate.trim() : undefined
      }


      await onSave(dealData)

      // For edit mode, let parent handle success message and closing
      if (!deal) {
        // Only handle success for create mode
        toast({
          title: "Success",
          description: "Deal created successfully"
        })

        setTimeout(() => {
          handleClose(false)
        }, 500)
      }
    } catch (error: any) {
      console.error('Error saving deal:', error)
      toast({
        title: "Error",
        description: error.message || "Failed to save deal",
        variant: "destructive"
      })
    } finally {
      setLoading(false)
    }
  }



  return (
    <Dialog open={open} onClose={() => handleClose(false)} maxWidth="lg" fullWidth>
      <DialogTitle>
        {deal ? `Edit Deal${customerName ? ` - ${customerName}` : ''}` : 'Create New Deal'}
        <Typography sx={{ fontSize: 14, color: "text.secondary", mt: 0.5 }}>
          Update deal information
        </Typography>
      </DialogTitle>

      <DialogContent>
        <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
          {/* Deal Information */}
          <Card>
            <CardHeader title={<Typography component="span" sx={{ fontSize: "1.125rem", fontWeight: 600 }}>Deal Information</Typography>} />
            <CardContent>
              <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                {/* Title field - Hidden for new deals, shown for editing */}
                {deal && (
                  <Box>
                    <FormLabel htmlFor="title" sx={{ display: "block", mb: 0.5 }}>Deal Title</FormLabel>
                    <TextField
                      id="title"
                      size="small"
                      fullWidth
                      value={title}
                      onChange={(e) => setTitle(e.target.value)}
                      placeholder="Enter deal title"
                    />
                  </Box>
                )}

                <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 2 }}>
                  <Box>
                    <FormLabel htmlFor="amount" sx={{ display: "block", mb: 0.5 }}>Equipment Price ($)</FormLabel>
                    <TextField
                      id="amount"
                      type="number"
                      size="small"
                      fullWidth
                      inputProps={{ step: "0.01", min: "0" }}
                      value={amount}
                      onChange={(e) => setAmount(e.target.value)}
                      placeholder="0.00"
                    />
                  </Box>

                  <Box>
                    <FormLabel htmlFor="maintenance" sx={{ display: "block", mb: 0.5 }}>Maintenance Percentage (%)</FormLabel>
                    <TextField
                      id="maintenance"
                      type="number"
                      size="small"
                      fullWidth
                      inputProps={{ step: "0.01", min: "0", max: "100" }}
                      value={maintenancePercentage}
                      onChange={(e) => setMaintenancePercentage(e.target.value)}
                      placeholder="0.00"
                    />
                  </Box>
                </Box>

                <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 2 }}>
                  <Box>
                    <FormLabel sx={{ display: "block", mb: 0.5 }}>Start Date *</FormLabel>
                    <DatePicker
                      value={startDate}
                      onChange={(date) => setStartDate(date)}
                    />
                  </Box>

                  <Box>
                    <FormLabel sx={{ display: "block", mb: 0.5 }}>Renewal Date</FormLabel>
                    <DatePicker
                      value={renewalDate}
                      onChange={(date) => setRenewalDate(date)}
                      placeholder="No renewal date"
                    />
                  </Box>
                </Box>


              </Box>
            </CardContent>
          </Card>


        </Box>

        {/* Footer Actions */}
        <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 1, pt: 2, mt: 2, borderTop: 1, borderColor: "divider" }}>
          <Button
            type="button"
            variant="outlined"
            color="inherit"
            onClick={() => handleClose(false)}
          >
            Cancel
          </Button>
          <Button
            type="button"
            variant="contained"
            onClick={handleSave}
            disabled={loading}
          >
            {loading ? 'Saving...' : (deal ? 'Update Deal' : 'Create Deal')}
          </Button>
        </Box>
      </DialogContent>
    </Dialog>
  )
}
